/**
 * Shared verification step component
 * Used by both borrower and investor signup flows
 */

import React from 'react';
import { ErrorDisplay } from '@/components/ErrorDisplay';
import { useCompleteVerification, useSendVerification } from '@/hooks/queries/useAuthQueries';
import { errorMessages } from '@/utils/formValidation/errorMessages';
import { validators } from '@/utils/formValidation/validators';
import { UserFormTypeRole } from '@/utils/user';
import { getBorrowerSignupData, getInvestorSignupData, useSignupStore } from '../../stores/signupStore';
import { SignupStepLayout } from './SignupStepLayout';
import { VerificationInputBlock } from './VerificationInputBlock';

type VerificationStepProps = {
  onBack: () => void;
  onSuccess: () => void;
};

export const VerificationStep: React.FC<VerificationStepProps> = ({ onBack, onSuccess }) => {
  const signupStore = useSignupStore();
  const completeVerificationMutation = useCompleteVerification();
  const sendVerificationMutation = useSendVerification();

  const {
    verificationCode,
    verificationErrors,
    showVerificationErrors,
    role,
    borrowerForm,
    investorForm,
    updateVerificationCode,
    setVerificationErrors,
    setShowVerificationErrors,
    twofaMethod,
  } = signupStore;

  const onResendEmailVerification = async () => {
    if (!role) {
      setVerificationErrors(['No role selected']);
      return;
    }

    setShowVerificationErrors(false);
    setVerificationErrors([]);

    try {
      const userData =
        role === UserFormTypeRole.BORROWER
          ? getBorrowerSignupData(borrowerForm.formData)
          : getInvestorSignupData(investorForm.formData);

      await sendVerificationMutation.mutateAsync(userData);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to resend verification email';
      setVerificationErrors([errorMessage]);
      setShowVerificationErrors(true);
    }
  };

  const validateVerificationCode = (): boolean => {
    const emailCodeError = errorMessages.verificationCode(verificationCode, 'Email verification code');

    if (emailCodeError) {
      setVerificationErrors([emailCodeError]);
      setShowVerificationErrors(true);
      return false;
    }

    setShowVerificationErrors(false);
    setVerificationErrors([]);
    return true;
  };

  const handleSubmit = async () => {
    if (!validateVerificationCode()) {
      return;
    }

    try {
      if (!role) {
        throw new Error('No role selected');
      }

      const email = role === UserFormTypeRole.BORROWER ? borrowerForm.formData.email : investorForm.formData.email;

      await completeVerificationMutation.mutateAsync({
        email,
        code: verificationCode,
        twofa_method: twofaMethod,
      });

      onSuccess();
    } catch (error) {
      console.error(error);
      const errorMessage = error instanceof Error ? error.message : 'Network error';
      setVerificationErrors([errorMessage]);
      setShowVerificationErrors(true);
    }
  };

  const isVerifying = completeVerificationMutation.isPending;
  const isResending = sendVerificationMutation.isPending;

  const errorList = verificationErrors;
  const showErrors = showVerificationErrors;

  return (
    <SignupStepLayout
      actions={{
        primaryLabel: 'Continue',
        onPrimaryClick: handleSubmit,
        onBack,
        primaryDisabled: isVerifying || !validators.verificationCode(verificationCode),
        primaryLoading: isVerifying,
        backDisabled: isVerifying,
      }}
      contentVariant="spacious"
    >
      <div className="flex flex-col gap-6 items-center justify-center w-full">
        <VerificationInputBlock
          title="Verify your email"
          subtitle="Please enter the code sent to your email"
          value={verificationCode}
          onChange={updateVerificationCode}
          maxLength={6}
          onResend={onResendEmailVerification}
          isResending={isResending}
        />

        {showErrors && <ErrorDisplay errors={errorList} />}
      </div>
    </SignupStepLayout>
  );
};

export default VerificationStep;
